import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ApolloCache,
  ApolloError,
  useApolloClient,
  useReactiveVar,
} from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  DocIssuePreviewQuery,
  DocTemplateDraftSampleDocument,
  DocTemplateDraftSampleQuery,
  DocTemplateDraftSampleQueryVariables,
  DocTemplatesListQuery,
  IssuedDocFileDownloadDocument,
  IssuedDocFileDownloadQuery,
  IssuedDocFileDownloadQueryVariables,
  IssuedDocsHistoryQuery,
  useCreateDocTemplateMutation,
  useDocIssuePreviewQuery,
  useDocSendDraftQuery,
  useSendIssuedDocPdfMutation,
  useDocTemplateEditorSourceQuery,
  useDocTemplatesListQuery,
  useIssueDocPdfMutation,
  useIssuedDocsHistoryQuery,
  useStoreDocTemplateMutation,
  useSwitchDocTemplateVersionMutation,
  useToggleDocTemplateMutation,
} from 'core/graphql';
import { userStateVar } from 'core/state/userState';
import { saveBase64File } from 'core/utils/fileDownload';
import { isPermissionError } from 'core/utils/isPermissionError';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import {
  IContractTemplateProblem,
  IContractTemplateToken,
  IContractTemplateVariable,
  templateEditorLanguage,
} from '../Esign/contractTemplateEditorModel';
import { useToasts } from '../Toast/useToasts';
import {
  documentErrorKey,
  documentPermissions,
  IDocumentField,
  IDocumentPermissions,
} from './documentModel';

/**
 * ТЗ 0112 (#155) — документы: шаблоны (Admin), предпросмотр, выпуск PDF и
 * история на карточках студента и группы. Скрытие кнопок — UX; права
 * проверяет сервер на каждом запросе.
 */

export type IDocumentTemplate = DocTemplatesListQuery['documentTemplates'][number];
export type IIssuedDocument = IssuedDocsHistoryQuery['issuedDocuments'][number];
export type IDocumentPreview = NonNullable<
  DocIssuePreviewQuery['previewDocument']
>;

const EMPTY_TEMPLATES: IDocumentTemplate[] = [];
const EMPTY_ISSUED: IIssuedDocument[] = [];
const EMPTY_TOKENS: IContractTemplateToken[] = [];
const EMPTY_VARIABLES: IContractTemplateVariable[] = [];
const EMPTY_PROBLEMS: IContractTemplateProblem[] = [];
const EMPTY_FIELDS: IDocumentField[] = [];

export const useDocumentPermissions = (): IDocumentPermissions => {
  const userState = useReactiveVar(userStateVar);
  return documentPermissions((userState.me?.roles ?? []) as string[]);
};

/** Текст отказа сервера на языке интерфейса. */
export const useDocumentErrorText = () => {
  const { t } = useTranslation();
  return useCallback(
    (error: { code?: string | null; message?: string | null }): string => {
      const key = documentErrorKey(error.code);
      return key
        ? t(key, { defaultValue: error.message || '' })
        : error.message || t('documents.errors.generic');
    },
    [t],
  );
};

/** После УДАЧНОЙ записи шаблона списки шаблонов перечитываются. */
export const evictDocumentTemplatesOnSuccess = (
  cache: Pick<ApolloCache<unknown>, 'evict' | 'gc'>,
  { data }: { data?: unknown },
): void => {
  const payload = data
    ? (Object.values(data as Record<string, unknown>)[0] as
        | { success?: boolean | null }
        | null
        | undefined)
    : null;
  if (!payload?.success) return;
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'documentTemplates' });
  cache.gc();
};

/** Справочник шаблонов документов. */
export const useDocumentTemplates = ({
  scope,
  includeInactive = false,
  skip = false,
}: {
  scope?: string | null;
  includeInactive?: boolean;
  skip?: boolean;
} = {}) => {
  const { data, loading, error, refetch } = useDocTemplatesListQuery({
    variables: { scope: scope || null, includeInactive },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  return {
    templates: data?.documentTemplates ?? EMPTY_TEMPLATES,
    loading,
    error,
    forbidden: isPermissionError(error),
    refetch,
  };
};

export interface IDocumentActionResult {
  ok: boolean;
  template: IDocumentTemplate | null;
  problems: IContractTemplateProblem[];
  errors: Array<{ field: string; code: string; message: string }>;
}

interface IRawTemplatePayload {
  success?: boolean | null;
  customErrors?: Array<{
    field?: string | null;
    code?: string | null;
    message?: string | null;
  } | null> | null;
  problems?: IContractTemplateProblem[] | null;
  template?: IDocumentTemplate | null;
}

const toResult = (
  payload: IRawTemplatePayload | null | undefined,
): IDocumentActionResult => ({
  ok: Boolean(payload?.success),
  template: payload?.template ?? null,
  problems: payload?.problems ?? EMPTY_PROBLEMS,
  errors: (payload?.customErrors ?? [])
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
    .map((item) => ({
      field: item.field ?? '',
      code: item.code ?? '',
      message: item.message ?? '',
    })),
});

const FAILED: IDocumentActionResult = {
  ok: false,
  template: null,
  problems: EMPTY_PROBLEMS,
  errors: [],
};

/** Тосты раздела документов (свой тип — дедупликация по типу). */
const useDocumentToast = () => {
  const { addToast } = useToasts();
  return useCallback(
    (type: ToastTypes, title: string, text?: string) =>
      addToast({
        data: { type, title, text },
        type: GlobalToastTypeNames.Documents,
      }),
    [addToast],
  );
};

/** Записи справочника шаблонов: создать, включить/выключить, откатить. */
export const useDocumentTemplateActions = () => {
  const { t } = useTranslation();
  const notify = useDocumentToast();
  const errorText = useDocumentErrorText();
  const [createMutation, { loading: creating }] = useCreateDocTemplateMutation({
    update: evictDocumentTemplatesOnSuccess,
  });
  const [toggleMutation, { loading: toggling }] = useToggleDocTemplateMutation({
    update: evictDocumentTemplatesOnSuccess,
  });
  const [
    switchMutation,
    { loading: activating },
  ] = useSwitchDocTemplateVersionMutation({
    update: evictDocumentTemplatesOnSuccess,
  });

  const crash = useCallback(
    (caught: unknown): IDocumentActionResult => {
      notify(
        ToastTypes.error,
        t('documents.toast.failed'),
        caught instanceof ApolloError && isPermissionError(caught)
          ? t('documents.errors.FORBIDDEN')
          : t('documents.toast.network'),
      );
      return FAILED;
    },
    [notify, t],
  );

  const report = useCallback(
    (result: IDocumentActionResult, okTitle: string) => {
      if (result.ok) {
        notify(ToastTypes.success, okTitle);
        return;
      }
      const lines = [...result.errors, ...result.problems].map(errorText);
      notify(
        ToastTypes.error,
        t('documents.toast.failed'),
        lines.filter(Boolean).join('\n') || undefined,
      );
    },
    [errorText, notify, t],
  );

  const create = useCallback(
    async (input: {
      name: string;
      kind?: string | null;
      scope?: string | null;
      basedOnId?: string | null;
    }): Promise<IDocumentActionResult> => {
      try {
        const { data } = await createMutation({
          variables: {
            name: input.name,
            kind: input.basedOnId ? null : input.kind || null,
            scope: input.basedOnId ? null : input.scope || null,
            basedOnId: input.basedOnId || null,
          },
        });
        const result = toResult(data?.createDocumentTemplate);
        if (result.ok) notify(ToastTypes.success, t('documents.toast.created'));
        return result;
      } catch (caught) {
        return crash(caught);
      }
    },
    [createMutation, crash, notify, t],
  );

  const setActive = useCallback(
    async (id: string, active: boolean): Promise<IDocumentActionResult> => {
      try {
        const { data } = await toggleMutation({ variables: { id, active } });
        const result = toResult(data?.setDocumentTemplateActive);
        report(
          result,
          active ? t('documents.toast.enabled') : t('documents.toast.disabled'),
        );
        return result;
      } catch (caught) {
        return crash(caught);
      }
    },
    [crash, report, t, toggleMutation],
  );

  const activateVersion = useCallback(
    async (versionId: string): Promise<IDocumentActionResult> => {
      try {
        const { data } = await switchMutation({ variables: { versionId } });
        const result = toResult(data?.activateDocumentTemplateVersion);
        report(result, t('documents.toast.versionActivated'));
        return result;
      } catch (caught) {
        return crash(caught);
      }
    },
    [crash, report, switchMutation, t],
  );

  return {
    create,
    setActive,
    activateVersion,
    busy: { creating, toggling, activating },
  };
};

/* ---------- editor source (ContractTemplateEditor, ТЗ 0107 → 0112) ---------- */

/** Текст шаблона для визуального редактора (только Admin). */
export const useDocumentTemplateEditable = (templateId: string | null) => {
  const { i18n } = useTranslation();
  const { canEditTemplates } = useDocumentPermissions();
  const language = templateEditorLanguage(i18n.language);
  const { data, loading, error, refetch } = useDocTemplateEditorSourceQuery({
    variables: { id: templateId || '', language },
    skip: !templateId || !canEditTemplates,
    fetchPolicy: 'network-only',
  });
  const editable = data?.documentTemplateEditable ?? null;
  const template = editable?.template ?? null;
  return {
    forbidden: !canEditTemplates || isPermissionError(error),
    editable,
    template: template
      ? {
          code: template.code,
          name: template.name,
          version: template.version,
          // Редактор правит действующую версию шаблона — плашка «это не
          // действующая версия» к документам не относится.
          isActive: true,
        }
      : null,
    html: editable?.html ?? '',
    tokens: (editable?.tokens as IContractTemplateToken[]) ?? EMPTY_TOKENS,
    variables:
      (editable?.variables as IContractTemplateVariable[]) ?? EMPTY_VARIABLES,
    language,
    loading,
    failed: Boolean(error) || (!loading && Boolean(data) && !editable),
    refetch,
  };
};

/** Образец несохранённого текста; последний запрос побеждает. */
export const useDocumentTemplateDraftSample = (templateId: string | null) => {
  const client = useApolloClient();
  const counter = useRef(0);
  const [state, setState] = useState<{
    html: string | null;
    problems: IContractTemplateProblem[];
    loading: boolean;
    failed: boolean;
  }>({ html: null, problems: EMPTY_PROBLEMS, loading: false, failed: false });

  const request = useCallback(
    async (html: string) => {
      if (!templateId) return;
      counter.current += 1;
      const mine = counter.current;
      setState((current) => ({ ...current, loading: true }));
      try {
        const { data } = await client.query<
          DocTemplateDraftSampleQuery,
          DocTemplateDraftSampleQueryVariables
        >({
          query: DocTemplateDraftSampleDocument,
          variables: { id: templateId, html },
          fetchPolicy: 'no-cache',
        });
        if (mine !== counter.current) return;
        const payload = data?.documentTemplatePreviewDraft;
        setState({
          html: payload?.html ?? null,
          problems: payload?.problems ?? EMPTY_PROBLEMS,
          loading: false,
          failed: false,
        });
      } catch (caught) {
        if (mine !== counter.current) return;
        setState((current) => ({ ...current, loading: false, failed: true }));
      }
    },
    [client, templateId],
  );

  useEffect(
    () => () => {
      counter.current += 1;
    },
    [],
  );

  return { ...state, request };
};

/** Запись новой версии из редактора. Токены документам не нужны. */
export const useDocumentTemplateSave = () => {
  const { t } = useTranslation();
  const notify = useDocumentToast();
  const [saveMutation, { loading: saving }] = useStoreDocTemplateMutation({
    update: evictDocumentTemplatesOnSuccess,
  });
  const save = useCallback(
    async (
      templateId: string,
      html: string,
      _tokens: IContractTemplateToken[],
      comment: string,
    ): Promise<IDocumentActionResult> => {
      try {
        const { data } = await saveMutation({
          variables: { id: templateId, html, comment: comment || null },
        });
        const result = toResult(data?.saveDocumentTemplate);
        if (result.ok) notify(ToastTypes.success, t('documents.toast.saved'));
        return result;
      } catch (caught) {
        notify(
          ToastTypes.error,
          t('documents.toast.failed'),
          caught instanceof ApolloError && isPermissionError(caught)
            ? t('documents.errors.FORBIDDEN')
            : t('documents.toast.network'),
        );
        return FAILED;
      }
    },
    [notify, saveMutation, t],
  );
  return { save, saving };
};

/* ---------- issue: preview, PDF, history ---------- */

export interface IDocumentTarget {
  templateId: string | null;
  studentId?: string | null;
  agreementId?: string | null;
  groupId?: string | null;
  issuePlace?: string;
}

/** Предпросмотр документа на живых данных (без записи в журнал). */
export const useDocumentPreview = (target: IDocumentTarget, skip = false) => {
  const { data, loading, error, refetch } = useDocIssuePreviewQuery({
    variables: {
      templateId: target.templateId || '',
      studentId: target.studentId || null,
      agreementId: target.agreementId || null,
      groupId: target.groupId || null,
      issuePlace: target.issuePlace || null,
    },
    skip: skip || !target.templateId,
    fetchPolicy: 'network-only',
  });
  const preview = data?.previewDocument ?? null;
  return {
    preview,
    missingFields: (preview?.missingFields ?? EMPTY_FIELDS) as IDocumentField[],
    hiddenFields: (preview?.hiddenFields ?? EMPTY_FIELDS) as IDocumentField[],
    loading,
    error,
    forbidden: isPermissionError(error),
    refetch,
  };
};

/** История выпусков по студенту или группе. */
export const useIssuedDocuments = (
  {
    studentId,
    groupId,
  }: { studentId?: string | null; groupId?: string | null },
  skip = false,
) => {
  const { data, loading, error, refetch } = useIssuedDocsHistoryQuery({
    variables: { studentId: studentId || null, groupId: groupId || null },
    skip: skip || (!studentId && !groupId),
    fetchPolicy: 'cache-and-network',
  });
  return {
    documents: data?.issuedDocuments ?? EMPTY_ISSUED,
    loading,
    error,
    forbidden: isPermissionError(error),
    refetch,
  };
};

export interface IIssueResult {
  ok: boolean;
  issued: IIssuedDocument | null;
  errors: Array<{ code: string; message: string }>;
}

/**
 * Выпуск: номер в журнал + PDF сразу браузеру. История перечитывается
 * (поле с аргументами на ROOT_QUERY выбрасывается целиком).
 */
export const useIssueDocument = () => {
  const { t } = useTranslation();
  const notify = useDocumentToast();
  const errorText = useDocumentErrorText();
  const [issueMutation, { loading: issuing }] = useIssueDocPdfMutation({
    update: (cache, { data }) => {
      if (!data?.issueDocument?.success) return;
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'issuedDocuments' });
      cache.gc();
    },
  });
  const issue = useCallback(
    async (target: IDocumentTarget): Promise<IIssueResult> => {
      if (!target.templateId) return { ok: false, issued: null, errors: [] };
      try {
        const { data } = await issueMutation({
          variables: {
            templateId: target.templateId,
            studentId: target.studentId || null,
            agreementId: target.agreementId || null,
            groupId: target.groupId || null,
            issuePlace: target.issuePlace || null,
          },
        });
        const payload = data?.issueDocument;
        const errors = (payload?.customErrors ?? [])
          .filter((item): item is NonNullable<typeof item> => Boolean(item))
          .map((item) => ({
            code: item.code ?? '',
            message: item.message ?? '',
          }));
        if (!payload?.success || !payload.issuedDocument) {
          notify(
            ToastTypes.error,
            t('documents.toast.issueFailed'),
            errors.map(errorText).filter(Boolean).join('\n') || undefined,
          );
          return { ok: false, issued: null, errors };
        }
        if (payload.file) saveBase64File(payload.file);
        notify(
          ToastTypes.success,
          t('documents.toast.issued', {
            number: payload.issuedDocument.number,
          }),
        );
        return { ok: true, issued: payload.issuedDocument, errors: [] };
      } catch (caught) {
        notify(
          ToastTypes.error,
          t('documents.toast.issueFailed'),
          caught instanceof ApolloError && isPermissionError(caught)
            ? t('documents.errors.FORBIDDEN')
            : t('documents.toast.network'),
        );
        return { ok: false, issued: null, errors: [] };
      }
    },
    [errorText, issueMutation, notify, t],
  );
  return { issue, issuing };
};

/** «Скачать» из истории: PDF рендерится заново по журналу выпуска. */
export const useIssuedDocumentDownload = () => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const notify = useDocumentToast();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const download = useCallback(
    async (id: string): Promise<boolean> => {
      setDownloadingId(id);
      try {
        const { data } = await client.query<
          IssuedDocFileDownloadQuery,
          IssuedDocFileDownloadQueryVariables
        >({
          query: IssuedDocFileDownloadDocument,
          variables: { id },
          fetchPolicy: 'no-cache',
        });
        const file = data?.issuedDocumentFile;
        if (!file) {
          notify(
            ToastTypes.error,
            t('documents.toast.downloadFailed'),
            t('documents.errors.FORBIDDEN'),
          );
          return false;
        }
        saveBase64File(file);
        return true;
      } catch (caught) {
        notify(
          ToastTypes.error,
          t('documents.toast.downloadFailed'),
          caught instanceof ApolloError && isPermissionError(caught)
            ? t('documents.errors.FORBIDDEN')
            : t('documents.toast.network'),
        );
        return false;
      } finally {
        setDownloadingId(null);
      }
    },
    [client, notify, t],
  );
  return { download, downloadingId };
};

/* ---------- send by e-mail (ТЗ 0112 §4) ---------- */

/**
 * Черновик письма с документом: адрес студента, польские тема и текст,
 * ящики «От». `network-only`: текст зависит от данных студента сейчас.
 */
export const useDocumentSendDraft = (issuedDocumentId: string | null) => {
  const { data, loading, error } = useDocSendDraftQuery({
    variables: { issuedDocumentId: issuedDocumentId || '' },
    skip: !issuedDocumentId,
    fetchPolicy: 'network-only',
  });
  return {
    draft: data?.documentSendDefaults ?? null,
    loading,
    error,
    forbidden: isPermissionError(error),
  };
};

export interface IDocumentSendResult {
  ok: boolean;
  conversationId: string | null;
  errors: Array<{ field: string; code: string; message: string }>;
}

/**
 * Отправка письма с PDF — только по явной кнопке. Отказ (ящик, адрес, Gmail)
 * окно рисует у полей; тост — успех и сбой сети/прав. Журнал отправок
 * документа обновляется нормализацией (`issuedDocument { id sends }`).
 */
export const useSendIssuedDocument = () => {
  const { t } = useTranslation();
  const notify = useDocumentToast();
  const [sendMutation, { loading: sending }] = useSendIssuedDocPdfMutation();
  const send = useCallback(
    async (input: {
      issuedDocumentId: string;
      to: string;
      subject: string;
      html: string;
      mailboxEmail: string | null;
    }): Promise<IDocumentSendResult> => {
      try {
        const { data } = await sendMutation({
          variables: {
            issuedDocumentId: input.issuedDocumentId,
            to: input.to.trim(),
            subject: input.subject.trim(),
            html: input.html,
            mailboxEmail: input.mailboxEmail || null,
          },
        });
        const payload = data?.sendIssuedDocument;
        const errors = (payload?.customErrors ?? [])
          .filter((item): item is NonNullable<typeof item> => Boolean(item))
          .map((item) => ({
            field: item.field ?? '',
            code: item.code ?? '',
            message: item.message ?? '',
          }));
        if (!payload?.success) {
          return { ok: false, conversationId: null, errors };
        }
        notify(
          ToastTypes.success,
          t('documents.send.toastSent', { to: input.to.trim() }),
        );
        return {
          ok: true,
          conversationId:
            payload.conversationId ?? payload.send?.conversationId ?? null,
          errors: [],
        };
      } catch (caught) {
        notify(
          ToastTypes.error,
          t('documents.send.toastFailed'),
          caught instanceof ApolloError && isPermissionError(caught)
            ? t('documents.errors.FORBIDDEN')
            : t('documents.toast.network'),
        );
        return { ok: false, conversationId: null, errors: [] };
      }
    },
    [notify, sendMutation, t],
  );
  return { send, sending };
};
